import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import * as markdown from '@attain-apps/fe/web/main/lib/markdown'

/**
 * Displays reasoning/thinking steps for an AI message.
 * Each message owns its own inline reasoning expansion state.
 */
@ngCore.Component({
  imports: [markdown.Component, ds.jsonTree.Component],
  selector: 'atn-assistant-message-info',
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component {
  /**
   * The array of info/reasoning steps to display
   */
  public readonly infoSg = ngCore.input.required<
    readonly ents.core.agent.Info[]
  >({
    alias: 'info',
  })

  /**
   * The status of the message (pending, completed, failed)
   */
  public readonly statusSg =
    ngCore.input.required<ents.core.agent.Message.Status>({
      alias: 'status',
    })

  /** Whether this message's reasoning panel is expanded inline. */
  public readonly expandedSg = ngCore.signal(false)

  /**
   * The info stream collapsed into display-ready steps:
   * - consecutive `status` entries sharing a `traceId` are merged into one
   *   step whose state flips from `active` to `done` when a follow-up
   *   status arrives.
   * - `toolUse` + `toolResult` entries sharing a `traceId` are paired into a
   *   single `tool` step whose state reflects invoking/completed/failed and
   *   exposes any error reason.
   */
  public readonly displayStepsSg = ngCore.computed<
    readonly ents.core.agent.Info.DisplayStep[]
  >(() => ents.core.agent.Info.groupInfo(this.infoSg(), this.statusSg()))

  /**
   * Short label describing the step currently in flight — surfaced next to
   * the trigger while the message is still pending and the panel is
   * collapsed, so the user has a hint of what the agent is doing without
   * needing to expand the reasoning. `undefined` when the message isn't
   * pending or no meaningful "current" step exists.
   */
  public readonly currentStepLabelSg = ngCore.computed<string | undefined>(
    () => {
      if (this.statusSg() !== 'pending') {
        return undefined
      }
      const steps = this.displayStepsSg()
      // Walk from the end to find the most recent step that still represents
      // active work (or, failing that, the latest step of any kind).
      for (let i = steps.length - 1; i >= 0; i -= 1) {
        const step = steps[i]
        if (!step) {
          continue
        }
        const label = ents.core.agent.Info.describeCurrentStep(step)
        if (label) {
          return label
        }
      }
      return undefined
    },
  )

  /**
   * Per-step selection of which tool payload tab is open (`request` /
   * `response` / none). Both default to collapsed; clicking a tab opens it
   * (and closes the sibling); clicking the active tab closes it.
   */
  public readonly payloadTabsSg = ngCore.signal<
    ReadonlyMap<number, 'request' | 'response'>
  >(new Map())

  /** The scrollable reasoning panel — auto-scrolled to bottom on updates. */
  private readonly _panelRef =
    ngCore.viewChild<ngCore.ElementRef<HTMLElement>>('panel')

  public constructor() {
    // Auto-scroll the reasoning panel to the bottom whenever a new step
    // arrives (and the panel is expanded) so the latest activity stays in
    // view during streaming.
    ngCore.effect(() => {
      const count = this.displayStepsSg().length
      const expanded = this.expandedSg()
      if (!expanded || count === 0) {
        return
      }
      const el = this._panelRef()?.nativeElement
      if (!el) {
        return
      }
      // Defer to the next frame so the new DOM has been laid out.
      queueMicrotask(() => {
        el.scrollTop = el.scrollHeight
      })
    })
  }

  /** Toggle this message's inline reasoning panel. */
  public toggleExpanded(): void {
    this.expandedSg.update(expanded => !expanded)
  }

  /**
   * Toggle a tool's request/response tab. Clicking the active tab closes it;
   * clicking the inactive tab swaps to it.
   */
  public togglePayloadTab(idx: number, tab: 'request' | 'response'): void {
    this.payloadTabsSg.update(current => {
      const next = new Map(current)
      if (next.get(idx) === tab) {
        next.delete(idx)
      } else {
        next.set(idx, tab)
      }
      return next
    })
  }
}
